import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  fetchProducts,  
  fetchProduct as fetchProductAPI,
  uploadProduct as addProductAPI,
  updateProduct as updateProductAPI,
  deleteProduct as deleteProductAPI,
  applyFilters
} from './productsAPI';

// Async thunks
export const fetchProductsAsync = createAsyncThunk(
  'products/fetchProducts',
  async () => {
    const response = await fetchProducts();
    return response;
  }
);

export const filterProductsAsync = createAsyncThunk(
  'products/filterProducts',
  async ({products, searchTerm, filters}) => {
    return applyFilters(products, searchTerm, filters);
  }
);

export const fetchProductAsync = createAsyncThunk(
  'products/fetchProduct',
  async (productId) => {
    const response = await fetchProductAPI(productId);
    return response;
  }
);


export const addProductAsync = createAsyncThunk(
  'products/addProduct',
  async (product) => {
    const response = await addProductAPI(product);
    return response;
  }
);

export const updateProductAsync = createAsyncThunk(
  'products/updateProduct',
  async (product) => {
    const response = await updateProductAPI(product);
    return response;
  }
);

export const deleteProductAsync = createAsyncThunk(
  'products/deleteProduct',
  async (productId) => {
    await deleteProductAPI(productId);
    return productId;
  }
);

const initialState = {
  products: [],
  filteredProducts: [], // Add this
  currentProduct: null,
  loading: false,
  error: null,
  searchTerm: '', // Add this
  filters: {      // Add this
    category: '',
    purity: '',
    sort: ''
  },
  currentPage:1
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
     // ...existing reducers...
     setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setFilter: (state, action) => {
      const { filterType, value } = action.payload;
      state.filters[filterType] = value;
      // Reset to page 1 when filter changes
      state.currentPage = 1;
    },
    resetFilters: (state) => {
      state.searchTerm = '';
      state.filters = { category: '', purity: '', sort: '' };
      state.filteredProducts = [...state.products];
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProductsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
        state.filteredProducts = action.payload;;
        state.currentPage = 1;
      })
      .addCase(fetchProductsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message || 'An error occurred while fetching products';
      })
      // Filter Products
      .addCase(filterProductsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(filterProductsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.filteredProducts = action.payload;
        state.currentPage = 1;
      })
      .addCase(filterProductsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message || 'An error occurred while fetching products';
      })
      // Fetch Single Product
      .addCase(fetchProductAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message || 'An error occurred while fetching the product';
      })
      // Add Product
      .addCase(addProductAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addProductAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.products.push(action.payload);
      })
      .addCase(addProductAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message || 'An error occurred while adding the product';
      })
      // Update Product
      .addCase(updateProductAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProductAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.products.findIndex(p => p.product_id === action.payload.product_id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        if (state.currentProduct && state.currentProduct.product_id === action.payload.product_id) {
          state.currentProduct = action.payload;
        }
      })
      .addCase(updateProductAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message || 'An error occurred while updating the product';
      })
      // Delete Product
      .addCase(deleteProductAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProductAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.products = state.products.filter(p => p.productId !== action.payload);
        if (state.currentProduct && state.currentProduct.productId === action.payload) {
          state.currentProduct = null;
        }
      })
      .addCase(deleteProductAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message || 'An error occurred while deleting the product';
      });
  }
});

export const {clearCurrentProduct, setSearchTerm, setFilter, resetFilters, setCurrentPage } = productsSlice.actions;
export default productsSlice.reducer;
